import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Checkbox, FormControlLabel, Button, useTheme, Grid,
    Typography, IconButton, Divider, TextField, MenuItem,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useCreateClass } from '../../hooks/classes/useCreateClass';
import { useUpdateClass } from '../../hooks/classes/useUpdateClass';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { useGetAcademicTerms } from '../../hooks/academicTerms/useGetAcademicTerms';
import { useGetLevels } from '../../hooks/levels/useGetLevels';
import { useGetTeachers } from '../../hooks/teachers/useGetTeachers';
import { useCurrentContext } from '../../hooks/useCurrentContext';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const DAYS_OF_WEEK = [
    { value: 'sunday', labelAr: 'الأحد', labelEn: 'Sunday' },
    { value: 'monday', labelAr: 'الإثنين', labelEn: 'Monday' },
    { value: 'tuesday', labelAr: 'الثلاثاء', labelEn: 'Tuesday' },
    { value: 'wednesday', labelAr: 'الأربعاء', labelEn: 'Wednesday' },
    { value: 'thursday', labelAr: 'الخميس', labelEn: 'Thursday' },
    { value: 'friday', labelAr: 'الجمعة', labelEn: 'Friday' },
    { value: 'saturday', labelAr: 'السبت', labelEn: 'Saturday' },
];

const CLASS_TYPES = [
    { id: 'kids', name: '' },
    { id: 'adults', name: '' },
];

const emptySchedule = () => ({ day_of_week: '', start_time: '', end_time: '' });

const FormAddEditClass = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const isArabic = i18n.language === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { createClass, isPending: isCreating } = useCreateClass();
    const { editClass, isUpdate } = useUpdateClass();
    const { isLoading: schoolsLoading, schools } = useGetSchools(1, 999);
    const { academicTerms } = useGetAcademicTerms(1, 999);
    const { levels } = useGetLevels(1, 999);
    const { teachers } = useGetTeachers(1, 999);
    const { currentSchoolId, currentTermId } = useCurrentContext();

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        school_id: '', academic_term_id: '', level_id: '', teacher_id: '',
        code: '', name: '', class_type: '', capacity: '', is_active: true,
    });
    const [schedules, setSchedules] = useState([emptySchedule()]);

    // Auto-set current context for new records
    useEffect(() => {
        if (!isEdit) {
            setForm((p) => ({
                ...p,
                school_id: p.school_id || currentSchoolId || '',
                academic_term_id: p.academic_term_id || currentTermId || '',
            }));
        }
    }, [currentSchoolId, currentTermId, isEdit]);

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            school_id: currentData.school_id || '',
            academic_term_id: currentData.academic_term_id || '',
            level_id: currentData.level_id || '',
            teacher_id: currentData.teacher_id || '',
            code: currentData.code || '',
            name: currentData.name || '',
            class_type: currentData.class_type || '',
            capacity: currentData.capacity ?? '',
            is_active: currentData.is_active ?? true,
        });
        if (currentData.schedules?.length) {
            setSchedules(currentData.schedules.map((s) => ({
                day_of_week: s.day_of_week || '',
                start_time: s.start_time || '',
                end_time: s.end_time || '',
            })));
        }
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') { setForm((p) => ({ ...p, [name]: checked })); return; }
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleScheduleChange = (idx, field, value) => {
        setSchedules((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
    };

    const addScheduleRow = () => setSchedules((prev) => [...prev, emptySchedule()]);
    const removeScheduleRow = (idx) => setSchedules((prev) => prev.filter((_, i) => i !== idx));

    const handleSubmit = (e) => {
        e.preventDefault();
        const validSchedules = schedules.filter((s) => s.day_of_week && s.start_time && s.end_time);
        const payload = {
            school_id: Number(form.school_id),
            academic_term_id: Number(form.academic_term_id),
            level_id: Number(form.level_id),
            teacher_id: Number(form.teacher_id),
            code: form.code || null,
            name: form.name,
            class_type: form.class_type,
            capacity: form.capacity ? Number(form.capacity) : null,
            is_active: !!form.is_active,
            schedules: validSchedules,
        };
        if (isEdit && currentData?.id) { editClass({ req: payload, id: currentData.id }); }
        else { createClass(payload); }
    };

    if (schoolsLoading) return <LoadingPage />;

    const schoolOpts = (schools || []).map((s) => ({ id: s.id, name: s.name }));
    const termOpts = (academicTerms || []).map((t) => ({ id: t.id, name: t.name }));
    const levelOpts = (levels || []).map((l) => ({ id: l.id, name: l.name }));
    const teacherOpts = (teachers || []).map((t) => ({ id: t.id, name: t.employee_name || `#${t.id}` }));
    const typeOpts = CLASS_TYPES.map((t) => ({ id: t.id, name: t.id === 'kids' ? (isArabic ? 'أطفال' : 'Kids') : (isArabic ? 'كبار' : 'Adults') }));

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'المدرسة' : 'School'} type="select" name="school_id"
                            value={form.school_id} onChange={(e) => setForm((p) => ({ ...p, school_id: e.target.value }))}
                            options={schoolOpts} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'الفصل الدراسي' : 'Academic Term'} type="select" name="academic_term_id"
                            value={form.academic_term_id} onChange={(e) => setForm((p) => ({ ...p, academic_term_id: e.target.value }))}
                            options={termOpts} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'المستوى' : 'Level'} type="select" name="level_id"
                            value={form.level_id} onChange={(e) => setForm((p) => ({ ...p, level_id: e.target.value }))}
                            options={levelOpts} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'المعلم' : 'Teacher'} type="select" name="teacher_id"
                            value={form.teacher_id} onChange={(e) => setForm((p) => ({ ...p, teacher_id: e.target.value }))}
                            options={teacherOpts} />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'كود الحلقة' : 'Class Code'} placeholder="CLS-01"
                            type="text" name="code" value={form.code} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'اسم الحلقة' : 'Class Name'}
                            placeholder={isArabic ? 'ادخل الاسم' : 'Enter name'}
                            type="text" name="name" value={form.name} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'النوع' : 'Type'} type="select" name="class_type"
                            value={form.class_type} onChange={(e) => setForm((p) => ({ ...p, class_type: e.target.value }))}
                            options={typeOpts} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'السعة' : 'Capacity'} placeholder="30"
                            type="number" name="capacity" value={form.capacity} onChange={handleChange} required={false} />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <FormControlLabel
                                control={<Checkbox checked={form.is_active} name="is_active" onChange={handleChange} size="small" />}
                                label={isArabic ? 'مفعّل' : 'Active'} />
                        </Box>
                    </Grid>
                </Grid>

                {/* Weekly Schedule Section */}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                        {isArabic ? 'الجدول الأسبوعي' : 'Weekly Schedule'}
                    </Typography>
                    <Button startIcon={<AddCircleIcon />} onClick={addScheduleRow} size="small" variant="outlined">
                        {isArabic ? 'إضافة موعد' : 'Add Slot'}
                    </Button>
                </Box>

                {schedules.map((schedule, idx) => (
                    <Grid container spacing={2} key={idx} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={4}>
                            <TextField select fullWidth size="small" value={schedule.day_of_week}
                                onChange={(e) => handleScheduleChange(idx, 'day_of_week', e.target.value)}
                                label={isArabic ? 'اليوم' : 'Day'}
                                dir={isArabic ? 'rtl' : 'ltr'}
                                sx={{ ...(isArabic && {
                                    '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                                    '& .MuiInputLabel-shrink': { right: 32 },
                                    '& .MuiSelect-icon': { left: 7, right: 'unset' },
                                    '& .MuiSelect-select': { textAlign: 'right', paddingRight: '14px !important', paddingLeft: '32px !important' },
                                    '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                                }) }}
                                SelectProps={{ MenuProps: { PaperProps: { sx: { direction: isArabic ? 'rtl' : 'ltr' } } } }}>
                                {DAYS_OF_WEEK.map((d) => (
                                    <MenuItem key={d.value} value={d.value}>
                                        {isArabic ? d.labelAr : d.labelEn}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField fullWidth size="small" type="time" value={schedule.start_time}
                                onChange={(e) => handleScheduleChange(idx, 'start_time', e.target.value)}
                                label={isArabic ? 'من' : 'From'} InputLabelProps={{ shrink: true }}
                                dir={isArabic ? 'rtl' : 'ltr'}
                                sx={{ ...(isArabic && {
                                    '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                                    '& .MuiInputLabel-shrink': { right: 32 },
                                    '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                                }) }} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField fullWidth size="small" type="time" value={schedule.end_time}
                                onChange={(e) => handleScheduleChange(idx, 'end_time', e.target.value)}
                                label={isArabic ? 'إلى' : 'To'} InputLabelProps={{ shrink: true }}
                                dir={isArabic ? 'rtl' : 'ltr'}
                                sx={{ ...(isArabic && {
                                    '& .MuiInputLabel-root': { left: 'unset', right: 32, transformOrigin: 'top right' },
                                    '& .MuiInputLabel-shrink': { right: 32 },
                                    '& .MuiOutlinedInput-notchedOutline legend': { textAlign: 'right' },
                                }) }} />
                        </Grid>
                        <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            {schedules.length > 1 && (
                                <IconButton color="error" onClick={() => removeScheduleRow(idx)} size="small">
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>
                ))}

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit}>
                        {loadingSubmit ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : isEdit ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'حفظ' : 'Save')}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditClass;
